import {
  dateTimeFromEpochMilliseconds,
  fromDateTime,
  getUserPreferenceValue,
  listAllReleaseNotes,
  ReleaseNote,
  ReleaseNoteStatus,
  todayDateTime,
  UserPreferenceKey,
} from '@rentguru/commons-utils';
import { query } from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useSession } from 'src/auth/client';
import { getUnreadReleaseNotes } from 'src/components/Announcement/announcementUtils';

import { useUser } from './UserContext';
import { useUserPreferences } from './UserPreferencesContext';

export interface ReleaseNotesContextState {
  releaseNotes: ReleaseNote[];
  unreadReleaseNotes: ReleaseNote[];
  markReleaseNotesRead: () => Promise<void>;
}

const ReleaseNotesContext = createContext<ReleaseNotesContextState | null>(null);

export const ReleaseNotesContextProvider = ({ children }: { children?: ReactNode }) => {
  const { isOwner } = useUser();
  const { data: session } = useSession();
  const { userPreferences, setPreferenceValue } = useUserPreferences();
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      try {
        setReleaseNotes(
          orderBy(
            await query<ReleaseNote[]>(listAllReleaseNotes, { status: ReleaseNoteStatus.PUBLISHED }),
            ['publishedAt'],
            ['desc']
          )
        );
      } catch {
        setReleaseNotes([]);
      }
    };
    fetchReleaseNotes();
  }, []);

  const readAt = getUserPreferenceValue(UserPreferenceKey.RELEASE_NOTES_READ_AT, userPreferences);
  const userCreatedAt = session?.user.createdAt;
  const userCreatedAtTimestamp = isNil(userCreatedAt)
    ? undefined
    : fromDateTime(dateTimeFromEpochMilliseconds(userCreatedAt.getTime()));

  const unreadReleaseNotes = useMemo<ReleaseNote[]>(
    () =>
      isNil(userCreatedAtTimestamp) ? [] : getUnreadReleaseNotes(releaseNotes, isOwner, readAt, userCreatedAtTimestamp),
    [releaseNotes, isOwner, readAt, userCreatedAtTimestamp]
  );

  const markReleaseNotesRead = useCallback(
    () => setPreferenceValue(UserPreferenceKey.RELEASE_NOTES_READ_AT, fromDateTime(todayDateTime())),
    [setPreferenceValue]
  );

  const value = useMemo<ReleaseNotesContextState>(
    () => ({ releaseNotes, unreadReleaseNotes, markReleaseNotesRead }),
    [releaseNotes, unreadReleaseNotes, markReleaseNotesRead]
  );

  return <ReleaseNotesContext.Provider value={value}>{children}</ReleaseNotesContext.Provider>;
};

export const useReleaseNotes = (): ReleaseNotesContextState => {
  const context = useContext(ReleaseNotesContext);
  if (isNil(context)) {
    throw new Error('`useReleaseNotes` hook must be used within a `ReleaseNotesContextProvider` component');
  }
  return context;
};
